import { ActionError, Button, Dialog, QrCode, Skeleton, plural, toast } from '@westhetics/ui'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import {
  patchPlanInCache,
  tokenOf,
  useOpenPhotoSession,
  usePhotoSessionStateReader,
  usePhotoTiming,
  useRevokePhotoSession,
  withLotPhoto,
  withStagePhoto,
  type LotSlot,
  type PhotoSession,
  type Stage,
  type TreatmentPlan,
} from './api'
import { QR_LINK_NOTE, QR_VALIDITY } from './labels'

/**
 * O QR de fotos pelo celular (RN-PLA-20 a 23): abre a sessão para uma etapa ou para os três slots
 * de um produto, mostra o código com "O código vale 1 hora." e consulta `GET /photos/state` a cada
 * 3 s para as fotos entrarem na tela sem recarregar (P-PLA-09). "Fechar" revoga a sessão. Ao lado
 * do código há "Copiar link", para quem está no celular sem leitor (decisão T41, que prevalece
 * sobre P-PLA-08).
 */

export type QrTarget = { target: 'stage'; stage: Stage } | { target: 'product'; productId: string }

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export function QrDialog({
  plan,
  target,
  title,
  onClose,
}: {
  plan: TreatmentPlan
  target: QrTarget
  /** O que o código abre: "Marcação" ou "Produto 1 · Juvéderm Volux". */
  title: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const open = useOpenPhotoSession(plan.id)
  const revoke = useRevokePhotoSession(plan.id)
  const readState = usePhotoSessionStateReader()
  const timing = usePhotoTiming()
  const [session, setSession] = useState<PhotoSession | null>(null)
  const [received, setReceived] = useState(0)
  const [ended, setEnded] = useState<'expired' | 'closed' | null>(null)
  const version = useRef<number | null>(null)
  const openMutate = open.mutate

  function start() {
    setEnded(null)
    setReceived(0)
    version.current = null
    openMutate(target, { onSuccess: setSession })
  }

  // Abrir a sessão ao montar; uma só vez por alvo.
  useEffect(() => {
    openMutate(target, { onSuccess: setSession })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const token = session?.url ? tokenOf(session.url) : null

  useEffect(() => {
    if (!token || ended) return
    let active = true
    const tick = async () => {
      try {
        const result = await readState(token, version.current)
        if (!active || result.notModified) return
        const state = result.data
        version.current = state.version
        if (state.newPhotos.length > 0) {
          setReceived((count) => count + state.newPhotos.length)
          patchPlanInCache(queryClient, plan.id, (current) =>
            state.newPhotos.reduce((acc, photo) => {
              if (target.target === 'stage') return withStagePhoto(acc, photo)
              return photo.slot && photo.slot !== 'expiry_date'
                ? withLotPhoto(acc, target.productId, photo.slot as LotSlot, photo)
                : acc
            }, current),
          )
        }
      } catch (error) {
        if (!active) return
        if (isApiError(error) && (error.code === 'link_expired' || error.code === 'link_invalid')) {
          setEnded('expired')
        } else if (isApiError(error) && error.code === 'conflict') {
          setEnded('closed')
        }
      }
    }
    const interval = setInterval(() => void tick(), timing.pollIntervalMs)
    return () => {
      active = false
      clearInterval(interval)
    }
  }, [token, ended, readState, timing.pollIntervalMs, queryClient, plan.id, target])

  function close() {
    if (session && !ended) {
      revoke.mutate(session.id, {
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: ['treatment-plans', 'detail', plan.id] })
        },
      })
    }
    onClose()
  }

  async function copy() {
    if (!session?.url) return
    toast(
      (await copyText(session.url))
        ? 'Link copiado.'
        : 'Não foi possível copiar. Selecione o endereço e copie com o teclado.',
    )
  }

  const receivedText =
    received === 0
      ? 'Nenhuma foto recebida ainda.'
      : `${plural(received, 'foto recebida', 'fotos recebidas')} nesta sessão.`

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) close()
      }}
      title={`Fotografar pelo celular · ${title}`}
      description="Leia o código com a câmera do celular. As fotos entram nesta tela na hora, sem recarregar."
      actions={
        <>
          {session?.url && !ended ? (
            <Button variant="text" size="sm" onClick={() => void copy()}>
              Copiar link
            </Button>
          ) : null}
          {ended ? (
            <Button
              variant="outline"
              size="sm"
              onClick={start}
              loading={open.isPending}
              loadingLabel="Abrindo…"
            >
              Abrir novo QR
            </Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={close}>
            Fechar
          </Button>
        </>
      }
    >
      {ended === 'expired' ? (
        <p className="font-body text-body text-ink font-light">
          O código venceu.{' '}
          {received > 0
            ? `As ${plural(received, 'foto enviada', 'fotos enviadas')} ficaram no plano.`
            : 'Abra um novo QR para continuar.'}
        </p>
      ) : ended === 'closed' ? (
        <p className="font-body text-body text-ink font-light">
          O plano foi encerrado; a sessão de fotos não recebe mais imagens.
        </p>
      ) : open.isError ? (
        <ActionError
          title="Não conseguimos abrir a sessão de fotos"
          description={errorMessage(open.error)}
          actions={
            <Button variant="outline" size="sm" onClick={start}>
              Tentar de novo
            </Button>
          }
        />
      ) : !session?.url ? (
        <div
          role="status"
          aria-label="Abrindo a sessão de fotos"
          className="flex flex-col items-center gap-4"
        >
          <Skeleton className="size-[200px]" />
          <Skeleton className="h-[11px] w-[140px]" />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 text-center">
          <QrCode value={session.url} label={`Código QR para fotografar ${title}`} />
          <p className="font-body text-eyebrow text-label font-light tracking-normal">
            {QR_VALIDITY}
          </p>
          <p
            role="status"
            aria-live="polite"
            className="font-body text-secondary text-[12px] leading-[1.6] font-light"
          >
            {receivedText}
          </p>
          <p className="font-body text-secondary max-w-[360px] text-[12px] leading-[1.6] font-light">
            {QR_LINK_NOTE}
          </p>
        </div>
      )}
    </Dialog>
  )
}
